import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, BarChart3, Target, Zap } from 'lucide-react';
import { caseStudies } from '../data/caseStudiesData';

const CARD_ACCENTS = [
  { icon: Shield, gradient: 'linear-gradient(135deg, #2693FF 0%, #1a75d2 100%)', metric: '95%+', metricLabel: 'Brand Accuracy' },
  { icon: BarChart3, gradient: 'linear-gradient(135deg, #1a75d2 0%, #1e6bbf 100%)', metric: '90%+', metricLabel: 'Manual Work Reduced' },
  { icon: Target, gradient: 'linear-gradient(135deg, #1e6bbf 0%, #2563eb 100%)', metric: '85%', metricLabel: 'P0 Features On Time' },
  { icon: Zap, gradient: 'linear-gradient(135deg, #2563eb 0%, #2693FF 100%)', metric: '$2B+', metricLabel: 'Annual Deal Volume' },
];

const FeaturedWork = () => {
  const refs = useRef([]);
  const featured = caseStudies.filter((cs) => cs.featured);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    refs.current.forEach((ref) => { if (ref) observer.observe(ref); });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          ref={(el) => (refs.current[0] = el)}
          className="reveal flex flex-col sm:flex-row sm:items-end sm:justify-between mb-14"
        >
          <div>
            <p className="text-sm font-medium uppercase tracking-widest mb-3 font-heading" style={{ color: '#2693FF' }}>
              Featured Work
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 font-heading">
              Selected case studies
            </h2>
          </div>
          <Link
            to="/case-studies"
            className="mt-4 sm:mt-0 inline-flex items-center gap-2 text-sm font-medium group"
            style={{ color: '#2693FF' }}
          >
            View all work
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {featured.map((study, index) => {
            const accent = CARD_ACCENTS[index] || CARD_ACCENTS[0];
            const Icon = accent.icon;

            return (
              <Link
                key={study.id}
                to={`/case-studies/${study.slug}`}
                ref={(el) => (refs.current[index + 1] = el)}
                className="reveal card-lift group rounded-2xl overflow-hidden border border-slate-100 bg-white block"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Metric header */}
                <div
                  className="relative px-7 py-8 sm:px-8 sm:py-10 overflow-hidden"
                  style={{ background: accent.gradient }}
                >
                  {/* Background icon */}
                  <Icon
                    className="absolute -right-4 -bottom-4 opacity-10"
                    style={{ width: '100px', height: '100px', color: '#fff' }}
                  />

                  <div className="relative flex items-end justify-between">
                    <div>
                      <div className="text-white/60 text-xs font-medium uppercase tracking-widest mb-1">
                        {accent.metricLabel}
                      </div>
                      <div className="text-4xl sm:text-5xl font-bold text-white font-heading">
                        {accent.metric}
                      </div>
                    </div>

                    {/* Categories */}
                    <div className="flex flex-wrap gap-1.5 justify-end">
                      {study.category.map((cat) => (
                        <span
                          key={cat}
                          className="text-xs px-2.5 py-1 rounded-full font-medium bg-white/15 text-white/90 backdrop-blur-sm"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 sm:p-7">
                  <h3 className="text-lg font-semibold text-slate-900 font-heading mb-2 group-hover:text-[#2693FF] transition-colors leading-snug">
                    {study.title}
                  </h3>

                  <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 mb-4">
                    {study.description}
                  </p>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="text-xs text-slate-400">
                      {study.details?.client && (
                        <span>{study.details.client}</span>
                      )}
                      {study.details?.timeline && (
                        <span> &middot; {study.details.timeline}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-xs font-medium" style={{ color: '#2693FF' }}>
                      Read more
                      <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedWork;
